import { formatTournamentDateTime } from '../utils/tournamentTime';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';

import { getAccessToken, clearAuthStorage } from '../services/api';
export default function TournamentLobby() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('leaderboard');
  const [joining, setJoining] = useState(false);
  const [playsRemaining, setPlaysRemaining] = useState(null);
  const [resultModal, setResultModal] = useState(null);
  const isLoggedIn = !!getAccessToken();

  useEffect(() => {
    loadTournament();
  }, [id]);

  const loadTournament = async () => {
    setLoading(true);
    try {
      const res = await api.tournaments.getById(id);
      const data = res.data?.data || res.data?.tournament;
      if (!data || data.game_type !== 'whot') { throw new Error('This tournament is not available for Whot.'); }
      setTournament(data);
      if (data.status === 'completed' && data.myResult) {
        const result = data.myResult;
        const seenKey = `9jawin_tournament_result_seen_${id}_${result.id}`;
        if (!sessionStorage.getItem(seenKey)) {
          setResultModal({ result, reward: result.reward || null });
          sessionStorage.setItem(seenKey, '1');
        }
      }

      // If logged in, check if joined
      if (isLoggedIn) {
        try {
          const myRes = await api.tournaments.getMyTournaments();
          const myT = (myRes.data.data || []).find(t => t.id === id);
          if (myT) {
            setPlaysRemaining(myT.playsRemaining);
          }
        } catch (e) { /* ignore */ }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!isLoggedIn) return navigate('/login');
    setJoining(true);
    try {
      await api.tournaments.join(id);
      await loadTournament();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to join');
    } finally {
      setJoining(false);
    }
  };

  const handleStartPlay = () => {
    navigate(`/tournaments/${id}/play`);
  };

  if (loading) return <Loading variant="page" label="Loading tournament…" />;

  if (error || !tournament) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
        <p>{error || 'Tournament not found'}</p>
        <Link to="/tournaments" style={{ color: '#00d4aa' }}>← Back to tournaments</Link>
      </div>
    );
  }

  const t = tournament;
  const entryFee = parseFloat(t.entry_fee || 0);
  const prizes = Array.isArray(t.prize_distribution) ? t.prize_distribution : (() => { try { return JSON.parse(t.prize_distribution || '[]'); } catch { return []; } })();
  const rankRewards = Array.isArray(t.rank_rewards) ? t.rank_rewards : prizes.map(p => ({ rank: Number(p.rank), amount: 0 }));
  const firstPrize = Number(rankRewards.find(p => Number(p.rank) === 1)?.amount || 0);
  const isJoined = playsRemaining !== null;
  const isLive = t.status === 'live';
  const completedParticipation = new URLSearchParams(window.location.search).get('completed') === '1';
  const canJoin = ['scheduled', 'live'].includes(t.status) &&
    !isJoined &&
    t.current_players < t.max_players;
  const canPlay = isJoined && isLive && (playsRemaining > 0);
  const getGameIcon = (type) => type === 'whot' ? '🃏' : '🎮';
  const getGameName = (type) => type === 'whot' ? 'Whot' : 'Archived tournament';

  const statusColors = {
    scheduled: '#f59e0b', live: '#10b981', closed: '#6b7280',
    completed: '#3b82f6', cancelled: '#ef4444',
  };

  const leaderboard = t.leaderboard || [];
  const participants = t.participants || [];
  const payouts = t.payouts || [];

  const getCurrentUsername = () => {
    try { return JSON.parse(atob(getAccessToken().split('.')[1])).username || 'Player'; } catch { return 'Player'; }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      {resultModal && (
        <div style={modalStyles.overlay} role="dialog" aria-modal="true" aria-label="Tournament result">
          <div style={modalStyles.card}>
            <div style={{ fontSize: 50 }}>🏆</div>
            {resultModal.reward && Number(resultModal.result.rank) === 1 ? (
              <>
                <h2 style={modalStyles.title}>🏆 League Winner!</h2>
                <p style={modalStyles.message}>🎉 Congratulations, {getCurrentUsername()}!{`\n\n`}You finished 1st place and won:{`\n\n`}₦{Number(resultModal.reward.amount).toLocaleString()}{`\n\n`}Your reward has been credited to your wallet.</p>
              </>
            ) : resultModal.reward ? (
              <>
                <h2 style={modalStyles.title}>🎉 Congratulations!</h2>
                <p style={modalStyles.message}>You finished #{resultModal.result.rank} in the tournament and won ₦{Number(resultModal.reward.amount).toLocaleString()}.{`\n\n`}Your reward has been credited to your wallet.</p>
              </>
            ) : (
              <>
                <h2 style={modalStyles.title}>🏆 Tournament Complete</h2>
                <p style={modalStyles.message}>You finished #{resultModal.result.rank} in the tournament.{`\n\n`}Keep playing and come back stronger in the next tournament! 🔥</p>
              </>
            )}
            <button type="button" onClick={() => setResultModal(null)} style={modalStyles.ok}>{resultModal.reward ? 'Okay' : 'View Results'}</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <Link to="/tournaments" style={{ color: '#888', textDecoration: 'none', fontSize: '14px' }}>
          ← Back to Tournaments
        </Link>
        {completedParticipation && (
          <Link to="/dashboard" style={{ color: '#000', background: '#f59e0b', padding: '9px 16px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px' }}>
            Back to Dashboard
          </Link>
        )}
      </div>

      {/* Header */}
      <div style={{
        background: '#1a1a2e', borderRadius: '12px', padding: '24px', marginTop: '16px',
        border: '1px solid #2a2a3e',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '32px' }}>{getGameIcon(t.game_type)}</span>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: '22px' }}>{t.name}</h1>
            <span style={{ color: statusColors[t.status] || '#aaa' }}>
              ● {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {canJoin && (
              <button onClick={handleJoin} disabled={joining}
                style={{
                  padding: '10px 24px', background: '#00d4aa', color: '#000',
                  borderRadius: '8px', border: 'none', cursor: 'pointer',
                  fontWeight: 'bold', fontSize: '15px',
                }}>
                {joining ? 'Joining...' : `Join (₦${entryFee.toLocaleString()})`}
              </button>
            )}
            {canPlay && (
              <button onClick={handleStartPlay}
                style={{
                  padding: '10px 24px', background: '#f59e0b', color: '#000',
                  borderRadius: '8px', border: 'none', cursor: 'pointer',
                  fontWeight: 'bold', fontSize: '15px',
                }}>
                ▶ Play (₦{entryFee.toLocaleString()})
              </button>
            )}
            {isJoined && playsRemaining === 0 && (
              <button type="button" onClick={() => window.alert(`🏆 Tournament Plays Completed\n\nYou've used all ${t.max_plays_per_player} of your ${t.max_plays_per_player} available plays for this tournament.\n\nYour results have been recorded on the leaderboard.`)}
                style={{ padding: '10px 16px', background: '#2a2a3e', color: '#aaa', borderRadius: '8px', border: '1px solid #444', cursor: 'default', fontWeight: 'bold', fontSize: '14px' }}>
                🔒 Play Limit Reached
              </button>
            )}
            {isJoined && isLive && playsRemaining === 1 && (
              <span style={{ color: '#f59e0b', padding: '10px 4px', fontWeight: '700' }}>
                1 play remaining · {t.max_plays_per_player - 1} of {t.max_plays_per_player} plays used
              </span>
            )}
            {isJoined && !isLive && playsRemaining !== null && playsRemaining > 0 && (
              <span style={{ color: '#888', padding: '10px' }}>
                {playsRemaining > 0 ? `${playsRemaining} plays remaining` : 'All plays used'}
              </span>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div style={{
          display: 'flex', gap: '24px', marginTop: '20px', flexWrap: 'wrap',
          fontSize: '14px', color: '#ccc',
        }}>
          <div>💰 Entry: <strong>₦{entryFee.toLocaleString()}</strong></div>
          <div>👥 Players: <strong>{Number(t.current_players ?? 0)}/{t.max_players}</strong></div>
          <div>🎮 Max Plays: <strong>{t.max_plays_per_player}</strong></div>
          <div>🎯 Actions/Game: <strong>{t.max_actions_per_game}</strong></div>
          {firstPrize > 0 ? (
            <div>🏆 First Prize Pool: <strong style={{ color: '#f59e0b' }}>₦{firstPrize.toLocaleString()}</strong></div>
          ) : null}
          <div>{t.payout_mode === 'auto' ? '🤖 Auto Payout' : '👀 Review Payout'}</div>
        </div>

        {/* Dates */}
        <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
          Starts: {formatTournamentDateTime(t.start_time)} — Ends: {formatTournamentDateTime(t.end_time)}
        </div>
      </div>

      {/* Tournament Rewards */}
      {prizes.length > 0 && (
        <div style={{ marginTop: '18px', background: '#1a1a2e', borderRadius: '12px', padding: '18px' }}>
          <h3 style={{ margin: '0 0 12px', color: '#fff' }}>🏆 Tournament Rewards</h3>
          {prizes.map((p, i) => {
            const rank = Number(p.rank);
            const amount = Number(rankRewards.find(r => Number(r.rank) === rank)?.amount || 0);
            const suffix = rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th';
            const medal = rank === 1 ? '🥇 ' : rank === 2 ? '🥈 ' : rank === 3 ? '🥉 ' : '';
            return <div key={p.rank ?? i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#ccc' }}><span>{medal}{rank}{suffix}</span><strong style={{ color: '#f59e0b' }}>₦{amount.toLocaleString()}</strong></div>;
          })}
        </div>
      )}

      {t.status === 'completed' && payouts.length > 0 && (
        <div style={{ marginTop: '18px', background: '#1a1a2e', borderRadius: '12px', padding: '18px', border: '1px solid #2a2a3e' }}>
          <h3 style={{ margin: '0 0 12px', color: '#fff' }}>🏆 Tournament Complete!</h3>
          <p style={{ margin: '0 0 12px', color: '#aaa', fontSize: 13 }}>Congratulations to our tournament winners! 🎉</p>
          {payouts.slice(0, 3).map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#ccc' }}>
              <span>{Number(p.rank) === 1 ? '🥇' : Number(p.rank) === 2 ? '🥈' : '🥉'} {p.username}</span>
              <strong style={{ color: '#f59e0b' }}>₦{Number(p.amount).toLocaleString()}</strong>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '8px', marginTop: '20px',
        borderBottom: '1px solid #2a2a3e', paddingBottom: '10px',
      }}>
        {['leaderboard', 'participants', 'payouts'].map(tabName => (
          <button
            key={tabName}
            onClick={() => setTab(tabName)}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: tab === tabName ? '#00d4aa' : 'transparent',
              color: tab === tabName ? '#000' : '#888',
              cursor: 'pointer', fontWeight: tab === tabName ? 'bold' : 'normal',
            }}
          >
            {tabName.charAt(0).toUpperCase() + tabName.slice(1)}
            {tabName === 'leaderboard' && leaderboard.length > 0 && ` (${leaderboard.length})`}
            {tabName === 'participants' && ` (${participants.length})`}
          </button>
        ))}
      </div>

      {/* Leaderboard */}
      {tab === 'leaderboard' && (
        <div style={{ marginTop: '12px' }}>
          {leaderboard.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
              {t.status === 'live' ? 'No plays recorded yet. Be the first!' : 'No plays recorded.'}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {leaderboard.map((entry, idx) => {
                const isMe = Boolean(entry.isYou);
                return (
                  <div key={entry.playId || idx} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    background: isMe ? '#1a2a2e' : '#1a1a2e',
                    borderRadius: '8px', padding: '12px 16px',
                    border: isMe ? '1px solid #00d4aa44' : '1px solid #2a2a3e',
                  }}>
                    <span style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: entry.rank <= 3 ? '#f59e0b' : '#2a2a3e',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold', fontSize: '14px',
                      color: entry.rank <= 3 ? '#000' : '#888',
                    }}>
                      {entry.rank}
                    </span>
                    <span style={{ flex: 1, fontWeight: isMe ? 'bold' : 'normal' }}>
                      {entry.username || 'Player'}
                      {isMe && ' (You)'}
                    </span>
                    <span style={{ color: '#00d4aa', fontWeight: 'bold', fontSize: '16px' }}>
                      {entry.score}
                    </span>
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      {entry.actionsUsed} actions
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Participants */}
      {tab === 'participants' && (
        <div style={{ marginTop: '12px' }}>
          {participants.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>No participants yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {participants.map(p => (
                <div key={p.id} style={{
                  background: '#1a1a2e', borderRadius: '8px', padding: '12px 16px',
                  border: '1px solid #2a2a3e',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span>{p.username || 'Player'}</span>
                  <span style={{ color: '#888', fontSize: '13px' }}>
                    {p.plays_used} plays used
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payouts */}
      {tab === 'payouts' && (
        <div style={{ marginTop: '12px' }}>
          {t.status !== 'completed' ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
              Payouts will be available when the tournament is completed.
            </p>
          ) : payouts.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
              Payouts have not been processed yet.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {payouts.map(p => (
                <div key={p.id} style={{
                  background: '#1a1a2e', borderRadius: '8px', padding: '12px 16px',
                  border: '1px solid #2a2a3e',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>#{p.rank}</span>
                    <span style={{ marginLeft: '8px' }}>{p.username || 'Player'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      ₦{parseFloat(p.amount).toLocaleString()}
                    </span>
                    <span style={{
                      padding: '4px 8px', borderRadius: '4px', fontSize: '12px',
                      background: p.status === 'paid' ? '#10b98133' : p.status === 'approved' ? '#f59e0b33' : '#6666',
                      color: p.status === 'paid' ? '#10b981' : p.status === 'approved' ? '#f59e0b' : '#888',
                    }}>
                      {p.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const modalStyles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.76)', display: 'grid', placeItems: 'center', padding: 20 },
  card: { width: 'min(460px, 100%)', boxSizing: 'border-box', background: '#1a1a2e', border: '1px solid #6b5418', borderRadius: 18, padding: 28, textAlign: 'center', boxShadow: '0 20px 70px rgba(0,0,0,0.5)' },
  title: { color: '#f59e0b', margin: '8px 0 14px' },
  message: { color: '#ddd', whiteSpace: 'pre-line', lineHeight: 1.6, fontSize: 15 },
  ok: { marginTop: 10, minWidth: 140, padding: '12px 24px', border: 0, borderRadius: 10, background: '#f59e0b', color: '#111', fontWeight: 800, cursor: 'pointer' },
};
